/**
 * AC detail page — header + status + Odoo + client + divisions summary
 * (table of rooms with BTU + chosen indoor) + kit lines + margin editor.
 * Reuses StatusDropdown / OdooQuoteDialog / MarginEditor.
 */

import { useEffect, useState } from "react"
import { useParams, Link } from "react-router-dom"
import { ChevronRight, Building2, Wind } from "lucide-react"
import type { AppShell } from "@/App"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { StatusDropdown } from "@/components/StatusDropdown"
import { OdooQuoteDialog } from "@/components/OdooQuoteDialog"
import { WinDialog } from "@/components/WinDialog"
import { WonOdooStatus } from "@/components/WonOdooStatus"
import { OwnerReassign } from "@/components/OwnerReassign"
import { MarginEditor } from "@/components/MarginEditor"
import { ProposalActionBar } from "@/components/ProposalActionBar"
import { KitEditor, type KitLineDraft } from "@/components/KitEditor"
import { getProposal, moduleLineFinalPrice } from "@/lib/api"
import { formatEur, odooOrderLabel } from "@/lib/format"

interface KitLine { name?: string; qty?: number | string; unit_price?: number | string; role?: string; notes?: string }
interface Division {
  name: string; area_m2: number; btu_required: number
  indoor_name: string | null; indoor_btu: number | null
  topology?: "mono" | "multi"
  /** Sistema MultiSplit (1, 2, …) — divisões do mesmo sistema partilham exterior. */
  multi_group?: number | null
}

/** "MonoSplit" / "MultiSplit 2" (numerado só quando há vários sistemas). */
function systemLabel(d: Division, multiSystems: number): string {
  if (d.topology === "mono") return "MonoSplit"
  if (d.topology !== "multi") return "—"
  return multiSystems > 1 ? `MultiSplit ${d.multi_group ?? 1}` : "MultiSplit"
}
interface AcProposal {
  id: string; number: string; status: string; kind: string
  client_name: string; client_email: string | null; client_phone: string | null
  address: string | null; city: string | null; postal_code: string | null
  applied_margin: number | string | null
  discount_fraction: number | string | null
  kit_lines_json: unknown
  total_price_no_vat_eur: number | string | null
  total_price_eur: number | string | null
  odoo_order_id: number | string | null; odoo_order_name: string | null; odoo_synced_at: string | null
  created_by?: string | null
  divisions_json?: unknown
  topology?: string | null
  brand?: string | null
  total_btu?: number | null
  outdoor_units_json?: unknown
}

const MIN_MARGIN = 1.25
const VAT = 1.23

export function AcDetail({ shell }: { shell: AppShell | null }) {
  const { id } = useParams<{ id: string }>()
  const [p, setP] = useState<AcProposal | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [changingStatus, setChangingStatus] = useState(false)
  const [odooOpen, setOdooOpen] = useState(false)
  const [winOpen, setWinOpen] = useState(false)
  const [editingKit, setEditingKit] = useState(false)

  const reload = () => {
    if (!shell || !id) return
    getProposal(shell, id).then((d) => setP(d as unknown as AcProposal)).catch((e) => setError(e instanceof Error ? e.message : "Falha"))
  }
  useEffect(() => {
    if (!shell || !id) return
    setLoading(true)
    getProposal(shell, id)
      .then((d) => setP(d as unknown as AcProposal))
      .catch((e) => setError(e instanceof Error ? e.message : "Falha"))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shell, id])

  async function handleStatusChange(next: string) {
    if (!shell || !p) return
    // «Ganho» só pelo «Fechar negócio» (relatório, fatura, fotos → Odoo).
    if (next === "ganho") { setWinOpen(true); return }
    setChangingStatus(true)
    try {
      const res = await shell.client.post<{ success: boolean; error?: string }>(
        "actions/change_status", { params: { id: p.id, status: next } },
      )
      if (!res.success) throw new Error(res.error)
      shell.bridge.toast({ variant: "success", description: "Estado actualizado" })
      setP((cur) => (cur ? { ...cur, status: next } : cur))
    } catch (err) {
      shell.bridge.toast({ variant: "error", description: err instanceof Error ? err.message : "Falha" })
    } finally {
      setChangingStatus(false)
    }
  }

  if (loading) return <p className="text-sm text-muted-foreground">A carregar…</p>
  if (error) return <p className="text-sm text-destructive">{error}</p>
  if (!p) return null

  const lines = (p.kit_lines_json as KitLine[]) ?? []
  const divisions = (p.divisions_json as Division[]) ?? []
  const multiSystems = new Set(
    divisions.filter((d) => d.topology === "multi").map((d) => d.multi_group ?? 1),
  ).size
  const margin = Number(p.applied_margin ?? 1.55) || 1.55

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <nav className="flex items-center gap-1 text-xs text-muted-foreground">
            <Link to="/list" className="hover:underline">Orçamentos</Link>
            <ChevronRight className="h-3 w-3" /><span>{p.number}</span>
          </nav>
          <h1 className="mt-1 flex items-center gap-3 text-2xl font-semibold tracking-tight">
            <Wind className="h-6 w-6 text-accent" />{p.number}
            <StatusDropdown value={p.status} onChange={handleStatusChange} loading={changingStatus} disabled={!shell || (p.status === "ganho" && !shell.isAdmin)} />
            {p.odoo_order_name ? (
              <span className="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300">
                <Building2 className="h-3 w-3" /> Odoo: {odooOrderLabel(p.odoo_order_name, p.odoo_order_id)}
              </span>
            ) : null}
          </h1>
        </div>
        <ProposalActionBar
          shell={shell}
          kind="ac"
          proposalId={p.id}
          number={p.number}
          clientName={p.client_name}
          clientEmail={p.client_email}
          odooOrderId={p.odoo_order_id}
          onOpenOdoo={() => setOdooOpen(true)}
          locked={p.status === "ganho"}
          onReload={reload}
          editingKit={editingKit}
          onToggleKit={() => setEditingKit((v) => !v)}
        />
      </div>

      <WonOdooStatus shell={shell} proposal={p as unknown as Record<string, unknown>} onDone={reload} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle>Cliente</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Field label="Nome" value={p.client_name} />
            <Field label="Email" value={p.client_email ?? "—"} />
            <Field label="Telefone" value={p.client_phone ?? "—"} />
            <Field label="Morada" value={[p.address, p.postal_code, p.city].filter(Boolean).join(" ") || "—"} />
            <OwnerReassign shell={shell} proposalId={p.id} ownerId={p.created_by} disabled={p.status === "ganho"} onChanged={reload} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Sistema</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 gap-3 text-sm">
            <Field label="Topologia" value={p.topology === "mono" ? "Mono-split" : p.topology === "mixed" ? "Mono + Multi-split" : p.topology === "multi" ? "Multi-split" : "—"} />
            <Field label="Marca" value={typeof p.brand === "string" && p.brand ? p.brand : "—"} />
            <Field label="Total BTU" value={p.total_btu ? p.total_btu.toLocaleString("pt-PT") : "—"} />
            <Field label="Divisões" value={String(divisions.length || "—")} />
          </CardContent>
        </Card>
      </div>

      {divisions.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Divisões</CardTitle></CardHeader>
          <CardContent className="px-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Divisão</TableHead>
                  <TableHead className="text-right">Área</TableHead>
                  <TableHead className="text-right">BTU</TableHead>
                  <TableHead>Sistema</TableHead>
                  <TableHead>Unidade interior</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {divisions.map((d, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-medium">{d.name}</TableCell>
                    <TableCell className="text-right">{d.area_m2} m²</TableCell>
                    <TableCell className="text-right">{(d.indoor_btu ?? d.btu_required)?.toLocaleString("pt-PT")}</TableCell>
                    <TableCell>{systemLabel(d, multiSystems)}</TableCell>
                    <TableCell>
                      {d.indoor_name ?? <Badge variant="outline" className="text-amber-600">sem unidade</Badge>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle>Componentes</CardTitle></CardHeader>
        <CardContent className={editingKit ? "" : "px-0"}>
          {editingKit ? (
            <KitEditor
              shell={shell}
              simulationId={p.id}
              kind="ac"
              brandSolarSlug={null}
              moduleBrand={typeof p.brand === "string" && p.brand.trim() ? p.brand : null}
              hasBattery={false}
              appliedMargin={margin}
              vatMultiplier={VAT}
              taxExemptionFactor={1}
              maxDiscountFraction={0.3}
              originalLines={toKitDraft(p.kit_lines_json)}
              originalDiscountFraction={Number(p.discount_fraction ?? 0)}
              originalTotalPriceEur={Number(p.total_price_eur ?? 0)}
              originalHidePayback={Boolean(
                (p as unknown as { hide_payback?: boolean }).hide_payback,
              )}
              onCancel={() => setEditingKit(false)}
              onSaved={() => { setEditingKit(false); reload() }}
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Descrição</TableHead>
                    <TableHead className="text-right">Qtd</TableHead>
                    <TableHead className="text-right">Preço Unit. (custo)</TableHead>
                    <TableHead className="text-right">Total c/ IVA</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((l, i) => {
                    const unit = Number(l.unit_price ?? 0)
                    const qty = Number(l.qty ?? 0)
                    return (
                      <TableRow key={i}>
                        <TableCell className="font-medium">{l.name}</TableCell>
                        <TableCell className="text-right">{qty}</TableCell>
                        <TableCell className="text-right">{formatEur(unit)}</TableCell>
                        <TableCell className="text-right font-semibold">
                          {formatEur(moduleLineFinalPrice({ role: l.role, cost: unit * qty, appliedMargin: margin, vatMultiplier: VAT, discountFraction: Number(p.discount_fraction ?? 0) }))}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
              <Separator className="my-2" />
              <div className="space-y-1 px-6 pb-4 text-sm">
                <PriceRow label="Subtotal sem IVA" value={formatEur(p.total_price_no_vat_eur)} />
                <PriceRow label="Total com IVA" value={formatEur(p.total_price_eur)} bold />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Margem comercial</CardTitle></CardHeader>
        <CardContent>
          {p.status === "ganho" ? (
            <p className="text-sm text-muted-foreground">Margem ×{margin.toFixed(2)} — proposta ganha, não editável.</p>
          ) : (
            <MarginEditor shell={shell} proposalId={p.id} appliedMargin={margin} minMargin={MIN_MARGIN} vatMultiplier={VAT} onSaved={() => reload()} />
          )}
        </CardContent>
      </Card>

      <WinDialog
        open={winOpen}
        onOpenChange={setWinOpen}
        shell={shell}
        kind={"ac"}
        proposalId={p.id}
        onWon={() => reload()}
        client={{
          name: p.client_name, email: p.client_email, phone: p.client_phone,
          address: p.address, city: p.city, postal_code: p.postal_code, proposal_number: p.number,
        }}
      />

      <OdooQuoteDialog
        open={odooOpen}
        onOpenChange={setOdooOpen}
        shell={shell}
        proposalId={p.id}
        existingOdoo={
          p.odoo_order_id != null && p.odoo_order_name
            ? { order_id: Number(p.odoo_order_id), order_name: p.odoo_order_name, synced_at: p.odoo_synced_at }
            : null
        }
        onSynced={() => reload()}
        client={{
          name: p.client_name, email: p.client_email, phone: p.client_phone,
          address: p.address, city: p.city, postal_code: p.postal_code, proposal_number: p.number,
        }}
      />
    </div>
  )
}

/** kit_lines_json (stored shape) → KitLineDraft[] para o editor inline. */
function toKitDraft(raw: unknown): KitLineDraft[] {
  const arr = Array.isArray(raw) ? (raw as Array<Record<string, unknown>>) : []
  return arr.map((l, i) => {
    const qty = Number(l.qty ?? 0)
    const unit = Number(l.unit_price ?? 0)
    return {
      uid: `${i}-${String(l.product_id ?? l.name ?? i)}`,
      type: (l.type as "product" | "manual") ?? (l.product_id != null ? "product" : "manual"),
      product_id: (l.product_id as number | null) ?? null,
      name: String(l.name ?? ""),
      description: typeof l.description === "string" ? l.description : undefined,
      qty,
      unit_price: unit,
      total: Number(l.total ?? qty * unit),
      role: typeof l.role === "string" ? l.role : undefined,
      position: Number(l.position ?? i),
      hidden_in_pdf: l.hidden_in_pdf === true,
    }
  })
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-sm font-medium">{value}</div>
    </div>
  )
}
function PriceRow({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className={`flex items-center justify-between text-sm ${bold ? "font-semibold" : ""}`}>
      <span>{label}</span><span>{value}</span>
    </div>
  )
}
